import { escapeRegExp } from 'lodash';
import { _keys } from '@epihub/core/src/fn/object';

type Interpolation<T> =
  | string
  | number
  | (() => string | number);

export function prompt<T>(
  strings: TemplateStringsArray,
  ...values: Interpolation<T>[]
) {
  return new PromptFromInterpolation<T>({ strings, values });
}

export function sprompt<T>(
  string: string,
  params?: T,
);
export function sprompt<T>(
  strings: TemplateStringsArray,
  ...values: Interpolation<T>[]
);
export function sprompt<T>(
  ...p:
    | [
      string: string,
      params?: T,
    ]
    | [
      strings: TemplateStringsArray,
      ...values: Interpolation<T>[],
    ]
) {
  if (typeof p[0] === 'string') {
    const string = p[0];
    return `${new PromptFromText<T>({ string, params: (p[1] ?? {}) as T })}`;
  }

  // Called as tagged template: sprompt`...`
  const [strings, ...values] = p as [TemplateStringsArray, ...Interpolation<T>[]];
  return `${new PromptFromInterpolation<T>({ strings, values })}`;
}

export abstract class Prompt<T> {
  abstract readonly toString: () => string;
}

export class PromptFromInterpolation<T> extends Prompt<T> {
  private readonly strings: TemplateStringsArray;
  private readonly values: Interpolation<T>[];

  constructor(
    interpolation: {
      strings: TemplateStringsArray;
      values: Interpolation<T>[];
    },
  ) {
    super();
    this.strings = interpolation.strings;
    this.values = interpolation.values;
  }

  readonly toString = () => {
    return this.strings.reduce((acc, str, i) => {
      const value = this.values[i];
      const resolved = typeof value === 'function' ? value() : (value ?? '');
      return acc + str + resolved;
    }, '');
  };
}

export class PromptFromText<T> extends Prompt<T> {
  private readonly string: string;
  private readonly params: T;

  constructor(
    value: {
      string: string;
      params: T;
    },
  ) {
    super();
    this.string = value.string;
    this.params = value.params;
  }

  readonly toString = () => {
    return _keys(this.params).reduce((acc, key) => {
      const result = acc.replace(
        new RegExp(escapeRegExp(`|{${key}}|`), 'g'),
        this.params[key],
      );
      return result;
    }, this.string);
  };
}

export type Promptable<T = any> =
  | Prompt<T>
  | string;
